/* Pure transform: runtime `RunResult` → UI `RunDisplay`.
 *
 * Splits stdout/stderr into semantic lines the Terminal renders with --out-*
 * colors, and formats the time/memory chrome. Kept pure (no Preact) so it is
 * unit-testable in isolation. */

import type { RunResult } from '../../runtime/run-result';
import type { RunDisplay, OutputLine, OutClass } from '../results/result-types';

// Internal script paths leak into PHP error messages; show the user's filename.
const SCRIPT_LABEL = 'script.php';
const PATH_RE = /\/(?:code|run)\.php/g;

/** Classify a stderr line by PHP's error-level prefix. */
function classify(line: string): OutClass {
  if (/\bDeprecated\b/.test(line)) return 'deprecated';
  if (/\b(?:Warning|Notice|Strict(?: Standards)?)\b/.test(line)) return 'warn';
  return 'err';
}

/** Split text into lines, dropping a single trailing newline's empty tail. */
function splitLines(text: string): string[] {
  if (!text) return [];
  const lines = text.split('\n');
  if (lines.length > 1 && lines[lines.length - 1] === '') lines.pop();
  return lines;
}

export function toRunDisplay(r: RunResult): RunDisplay {
  const lines: OutputLine[] = [];

  for (const t of splitLines(r.stdout)) {
    lines.push({ t: t.replace(PATH_RE, SCRIPT_LABEL) });
  }
  for (const raw of splitLines(r.stderr)) {
    const t = raw.replace(PATH_RE, SCRIPT_LABEL);
    if (t.trim() === '') continue;
    lines.push({ t, c: classify(t) });
  }

  return {
    lines,
    timeMs: r.timeMs,
    memMB: (r.memBytes / 1048576).toFixed(2),
    exitCode: r.exitCode,
  };
}

/** A one-line error card for when the runtime itself failed to run a snippet. */
export function errorDisplay(message: string): RunDisplay {
  return {
    lines: [{ t: message, c: 'err' }],
    timeMs: 0,
    memMB: '0.00',
    exitCode: 1,
  };
}
